import {
	AttachMoneyOutlined,
	ContentCopy,
	Download,
	HighlightOff,
	History,
	MoneyOff,
} from '@mui/icons-material';
import {
	GridActionsCellItem,
	GridColDef,
	GridPaginationModel,
	GridRenderCellParams,
	GridRowParams,
} from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	getCatchUpQueryKey,
	getPaginatedCatchUpsQueryKey,
	useInvalidateInvoiceMutation,
	usePaginatedCatchUps,
	useWriteOffInvoiceMutation,
} from '../../../api/CatchUpsApi';
import {
	usePayInvoiceMutation,
	useReverseInvoiceMutation,
} from '../../../api/InvoiceApi';
import { getUsePolicyQueryKey, usePolicy } from '../../../api/PolicyApi';
import { useAuthContext } from '../../../context/auth';
import { CatchUpInvoice } from '../../../types/catchUps';
import { CurrencyCode } from '../../../types/currencyCode';
import { Payment } from '../../../types/payments';
import { PaymentReversalReason } from '../../../types/policy';
import { formatCurrency } from '../../../util/util';
import { LinkCell } from '../../grid';
import GridWithToolbar from '../../grid/GridWithToolbar';
import InvoiceStateChip from '../../InvoiceStateChip';
import PayInvoiceModal from '../../payments/components/PayInvoiceModal';
import NewReversePaymentModal from '../../payments/components/ReversePaymentModal';
import WriteOffPaymentModal from '../../payments/components/WriteOffPaymentModal';
import { infoToast } from '../../Toast';
import InvalidateInvoiceModal from '../components/InvalidateInvoiceModal';
import {
	CATCHUPS_PAGE_TITLE,
	CATCHUPS_PAGE__CREATE_BUTTON,
	CATCHUPS_PAGE__TABLE_ACTION__COPY,
	CATCHUPS_PAGE__TABLE_ACTION__COPY__SUCCESS,
	CATCHUPS_PAGE__TABLE_ACTION__DOWNLOAD,
	CATCHUPS_PAGE__TABLE_ACTION__INVALIDATE,
	CATCHUPS_PAGE__TABLE_ACTION__PAY,
	CATCHUPS_PAGE__TABLE_ACTION__REVERSE,
	CATCHUPS_PAGE__TABLE_ACTION__WRITE_OFF,
	CATCHUPS_PAGE__TABLE_HEADER__AMOUNT,
	CATCHUPS_PAGE__TABLE_HEADER__CREATED_DATE,
	CATCHUPS_PAGE__TABLE_HEADER__DISPLAY_ID,
	CATCHUPS_PAGE__TABLE_HEADER__STATUS,
} from '../data/constants';

interface ModalInfo {
	open: boolean;
	catchUp?: CatchUpInvoice;
}

interface CatchUpInvoiceRow extends CatchUpInvoice {
	catchUp: CatchUpInvoice;
}

const CatchUpsPage = () => {
	const authData = useAuthContext();
	const params = useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const policyLocator = params.policyId;
	const policyholderLocator = params.policyholderId;
	const { auth, hasPermission } = authData;
	const authToken = auth.authorizationToken;
	const [page, setPage] = React.useState({
		page: 0,
		pageSize: 25,
	});
	const [reverseInvoiceModalInfo, setReverseInvoiceModalInfo] =
		React.useState<ModalInfo>(null);
	const [payInvoiceModalInfo, setPayInvoiceModalInfo] =
		React.useState<ModalInfo>(null);
	const [writeOffInvoiceModalInfo, setWriteOffInvoiceModalInfo] =
		React.useState<ModalInfo>(null);
	const [invalidateInvoiceModalInfo, setInvalidateInvoiceModalInfo] =
		React.useState<ModalInfo>(null);

	const { data: policyResponse } = usePolicy(
		policyLocator,
		authData?.auth?.authorizationToken,
	);
	const {
		data: catchUpInvoiceResponse,
		isLoading,
		fetchNextPage,
		fetchPreviousPage,
		isFetchingNextPage,
		hasNextPage,
	} = usePaginatedCatchUps(
		page.pageSize,
		authToken,
		policyholderLocator,
		policyLocator,
	);
	const reverseInvoiceMutation = useReverseInvoiceMutation(
		authToken,
		queryClient,
	);
	const payInvoiceMutation = usePayInvoiceMutation(authToken, queryClient);
	const writeOffInvoiceMutation = useWriteOffInvoiceMutation(
		authToken,
		queryClient,
	);
	const invalidateInvoiceMutation = useInvalidateInvoiceMutation(
		authToken,
		queryClient,
	);

	const refetchData = (policyId?: string, catchUp?: CatchUpInvoice) => {
		const policyQueryKey = getUsePolicyQueryKey(policyId, authToken);
		const invoiceQueryKey = getCatchUpQueryKey(catchUp.locator, authToken);

		queryClient.invalidateQueries(invoiceQueryKey);
		queryClient.invalidateQueries(policyQueryKey);
		if (page.pageSize) {
			queryClient.invalidateQueries(
				getPaginatedCatchUpsQueryKey(
					page.pageSize,
					authToken,
					policyholderLocator,
					policyLocator,
				),
			);
			queryClient.invalidateQueries(
				getPaginatedCatchUpsQueryKey(
					page.pageSize,
					authToken,
					undefined,
					policyLocator,
				),
			);
			queryClient.invalidateQueries(
				getPaginatedCatchUpsQueryKey(
					page.pageSize,
					authToken,
					policyholderLocator,
					undefined,
				),
			);
		}
	};

	const reversePayment = async (
		paymentLocator: string,
		policyLocator: string,
		reversalReason: PaymentReversalReason,
	) => {
		await reverseInvoiceMutation.mutateAsync({
			paymentLocator,
			reversalReason,
		});
		refetchData(policyLocator, reverseInvoiceModalInfo.catchUp);
	};

	const payInvoice = async (
		policyId: string,
		locator: string,
		paymentCreateReq: any,
	): Promise<Payment> => {
		const response = await payInvoiceMutation.mutateAsync({
			paymentCreateReq,
			invoiceLocator: locator,
			isCatchUpInvoice: true,
		});
		refetchData(policyId, payInvoiceModalInfo.catchUp);
		return response.data;
	};
	const invalidateInvoice = async (
		invoiceLocator: string,
		policyLocator: string,
	) => {
		await invalidateInvoiceMutation.mutateAsync({ invoiceLocator });
		refetchData(policyLocator, invalidateInvoiceModalInfo.catchUp);
	};

	const writeOffInvoice = async (
		invoiceLocator: string,
		policyLocator: string,
	) => {
		await writeOffInvoiceMutation.mutateAsync({ invoiceLocator });
		refetchData(policyLocator, writeOffInvoiceModalInfo.catchUp);
	};

	const currPage = catchUpInvoiceResponse?.pages[page.page];
	const catchUps = currPage?.data.catchUps || [];

	const StateChip = (params: GridRenderCellParams<CatchUpInvoice>) => {
		const invoiceStatus =
			params.row.settlementStatus === 'settled'
				? params.row.settlementType
				: params.row.settlementStatus;
		return <InvoiceStateChip state={invoiceStatus} />;
	};

	const getRowActions = (params: GridRowParams<CatchUpInvoiceRow>) => {
		const rowActions: JSX.Element[] = [];

		if (
			params.row.settlementStatus === 'settled' &&
			params.row.settlementType === 'paid'
		) {
			rowActions.push(
				<GridActionsCellItem
					icon={<History />}
					onClick={() =>
						setReverseInvoiceModalInfo({
							open: true,
							catchUp: params.row.catchUp,
						})
					}
					label={CATCHUPS_PAGE__TABLE_ACTION__REVERSE}
					showInMenu
				/>,
			);
		}
		if (params.row.settlementStatus === 'outstanding') {
			if (hasPermission('invoice.pay')) {
				rowActions.push(
					<GridActionsCellItem
						icon={<AttachMoneyOutlined />}
						onClick={() =>
							setPayInvoiceModalInfo({
								open: true,
								catchUp: params.row.catchUp,
							})
						}
						label={CATCHUPS_PAGE__TABLE_ACTION__PAY}
						showInMenu
					/>,
				);
			}
			if (hasPermission('catchUpInvoice.writeOff')) {
				rowActions.push(
					<GridActionsCellItem
						icon={<MoneyOff />}
						onClick={() =>
							setWriteOffInvoiceModalInfo({
								open: true,
								catchUp: params.row.catchUp,
							})
						}
						label={CATCHUPS_PAGE__TABLE_ACTION__WRITE_OFF}
						showInMenu
					/>,
				);
			}
			rowActions.push(
				<GridActionsCellItem
					icon={<HighlightOff />}
					onClick={() =>
						setInvalidateInvoiceModalInfo({
							open: true,
							catchUp: params.row.catchUp,
						})
					}
					label={CATCHUPS_PAGE__TABLE_ACTION__INVALIDATE}
					showInMenu
				/>,
			);
		}

		rowActions.push(
			<GridActionsCellItem
				icon={<Download />}
				onClick={() => {
					window.open(params.row.documents[0].url, '_blank');
				}}
				label={CATCHUPS_PAGE__TABLE_ACTION__DOWNLOAD}
				showInMenu
			/>,
		);
		rowActions.push(
			<GridActionsCellItem
				icon={<ContentCopy />}
				onClick={() => {
					navigator.clipboard.writeText(params.row.locator);
					infoToast(CATCHUPS_PAGE__TABLE_ACTION__COPY__SUCCESS);
				}}
				label={CATCHUPS_PAGE__TABLE_ACTION__COPY}
				showInMenu
			/>,
		);
		return rowActions;
	};

	const columns: GridColDef[] = [
		{
			field: 'displayId',
			headerName: CATCHUPS_PAGE__TABLE_HEADER__DISPLAY_ID,
			flex: 0.2,
			sortable: false,
			renderCell: (params) => (
				<LinkCell label={params.value} to={params.row.locator} />
			),
		},
		{
			field: 'amount',
			headerName: CATCHUPS_PAGE__TABLE_HEADER__AMOUNT,
			flex: 0.2,
			sortable: false,
			renderCell: (params) => params.row.amountFormatted,
		},
		{
			field: 'state',
			headerName: CATCHUPS_PAGE__TABLE_HEADER__STATUS,
			flex: 0.2,
			sortable: false,
			renderCell: StateChip,
		},
		{
			field: 'createdTimestamp',
			headerName: CATCHUPS_PAGE__TABLE_HEADER__CREATED_DATE,
			sortable: false,
			flex: 0.2,
		},
		{
			field: 'actions',
			type: 'actions',
			align: 'right',
			flex: 0.2,
			sortable: false,
			getActions: getRowActions,
		},
	];

	const handlePageChange = (model: GridPaginationModel) => {
		if (model.pageSize !== page.pageSize) {
			setPage({
				page: 0,
				pageSize: model.pageSize,
			});
		} else {
			setPage({
				page: model.page,
				pageSize: model.pageSize,
			});
			if (model.page > page.page) {
				fetchNextPage();
			}
			if (model.page < page.page) {
				fetchPreviousPage();
			}
		}
	};

	const formattedData = catchUps.map((catchUp) => {
		return {
			...catchUp,
			amount: catchUp.totalDue,
			amountFormatted: formatCurrency(
				parseFloat(catchUp.totalDue),
				catchUp.totalDueCurrency as CurrencyCode,
			),
			createdTimestamp: catchUp.createdTimestamp
				? authData.getZonedFormattedDateString(catchUp.createdTimestamp)
				: '',
			catchUp: catchUp,
		};
	});

	const hasNegativeCashBalance =
		policyResponse?.cashBalance?.amount &&
		parseFloat(policyResponse?.cashBalance?.amount) < 0;
	const canCreateCatchUp =
		policyLocator &&
		hasNegativeCashBalance &&
		authData.hasPermission('catchUpInvoice.issue');
	return (
		<>
			<GridWithToolbar
				rowId='locator'
				title={CATCHUPS_PAGE_TITLE}
				key={catchUps?.length}
				data={formattedData || []}
				columns={columns}
				loading={isLoading || isFetchingNextPage}
				buttonColor={'primary'}
				buttonText={CATCHUPS_PAGE__CREATE_BUTTON}
				onButtonClick={canCreateCatchUp ? () => navigate('new') : null} // sending null will not display button
				paginationMode='server'
				rowCount={1000}
				paginationModel={page}
				onPaginationModelChange={handlePageChange}
				hasNextPage={
					hasNextPage || catchUpInvoiceResponse?.pages?.length > page.page + 1
				}
			/>
			{reverseInvoiceModalInfo?.catchUp && (
				<NewReversePaymentModal
					callback={reversePayment}
					open={reverseInvoiceModalInfo.open}
					handleClose={() => {
						setReverseInvoiceModalInfo({
							open: false,
							catchUp: null,
						});
					}}
					invoice={reverseInvoiceModalInfo.catchUp}
					isCatchUp
				/>
			)}
			{payInvoiceModalInfo?.catchUp && (
				<PayInvoiceModal
					callback={payInvoice}
					isCallbackLoading={false}
					isCallbackSuccess={false}
					open={payInvoiceModalInfo.open}
					setOpen={() => {
						setPayInvoiceModalInfo({
							open: false,
						});
					}}
					invoice={payInvoiceModalInfo.catchUp}
					isCatchUp
				/>
			)}
			{writeOffInvoiceModalInfo?.catchUp && (
				<WriteOffPaymentModal
					callback={writeOffInvoice}
					open={writeOffInvoiceModalInfo.open}
					handleClose={() => {
						setWriteOffInvoiceModalInfo({
							open: false,
							catchUp: null,
						});
					}}
					invoice={writeOffInvoiceModalInfo.catchUp}
					isCatchUp
				/>
			)}
			{invalidateInvoiceModalInfo?.catchUp && (
				<InvalidateInvoiceModal
					callback={invalidateInvoice}
					open={invalidateInvoiceModalInfo.open}
					handleClose={() => {
						setInvalidateInvoiceModalInfo({
							open: false,
							catchUp: null,
						});
					}}
					invoice={invalidateInvoiceModalInfo.catchUp}
					isCatchUp
				/>
			)}
		</>
	);
};

export default CatchUpsPage;
